import React, { useMemo, useState } from 'react';
import PageTitle from '../../FormElements/PageTitle';
import getLandingImageHeight from '../../../utils/getLandingImageHeight';
import htmlToPlainTextWithLists from '../../../utils/htmlToPlainTextWithLists';
import { useAuth } from '../../../contexts/AuthContext';

const MAX_WHITEBOARDS = 3;

export default function WhiteboardPage() {
	const { user, csrfToken, endpoints } = useAuth();
	const [boards, setBoards] = useState(['']);
	const [boardIds, setBoardIds] = useState([]);
	const [statusMessage, setStatusMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [isSavingAi, setIsSavingAi] = useState(false);

	const landingHeight = useMemo(() => {
		const baseSize = getLandingImageHeight();
		return {
			width: '100%',
			height: baseSize.height || baseSize.maxHeight || 650
		};
	}, []);

	const addWhiteboard = () => {
		if (boards.length >= MAX_WHITEBOARDS) return;
		setBoards((prevBoards) => [...prevBoards, '']);
		setBoardIds((prev) => [...prev, null]);
	};

	const removeWhiteboard = () => {
		if (boards.length <= 1) return;
		setBoards((prevBoards) => prevBoards.slice(0, -1));
		setBoardIds((prev) => prev.slice(0, -1));
	};

	const updateBoard = (index, value) => {
		setBoards((prevBoards) =>
			prevBoards.map((board, boardIndex) =>
				boardIndex === index ? value : board
			)
		);
	};

	const buildPayload = (withAi) => ({
		whiteboards: boards.map((content, index) => ({
			id: boardIds[index] != null ? boardIds[index] : null,
			content,
			position: index
		})),
		with_ai: withAi
	});

	const mergeRowsIntoPanels = (rows) => {
		if (!Array.isArray(rows)) return;
		const positionOf = (row) => {
			const v = row.position != null ? row.position : row['position'];
			const n = typeof v === 'number' ? v : parseInt(String(v), 10);
			return Number.isNaN(n) ? null : n;
		};
		const idOf = (row) => {
			const v = row.id != null ? row.id : row['id'];
			if (v === null || v === undefined || v === '') return null;
			const n = typeof v === 'number' ? v : parseInt(String(v), 10);
			return Number.isNaN(n) ? null : n;
		};
		const contentOf = (row) => {
			const v = row.content != null ? row.content : row['content'];
			if (v == null) return '';
			return htmlToPlainTextWithLists(String(v));
		};
		setBoards((prevBoards) => {
			const next = [...prevBoards];
			rows.forEach((row) => {
				const p = positionOf(row);
				if (p == null || p < 0) return;
				while (next.length <= p) next.push('');
				next[p] = contentOf(row);
			});
			return next;
		});
		setBoardIds((prevIds) => {
			const next = [...prevIds];
			rows.forEach((row) => {
				const p = positionOf(row);
				if (p == null || p < 0) return;
				while (next.length <= p) next.push(null);
				next[p] = idOf(row);
			});
			return next;
		});
	};

	const replaceUserId = (template) => template.replace('user_id', String(user.id));

	const postWhiteboards = async ({ withAi }) => {
		setErrorMessage('');
		setStatusMessage('');
		if (withAi) {
			setIsSavingAi(true);
		} else {
			setIsSaving(true);
		}
		try {
			const response = await fetch(
				replaceUserId(endpoints.upsert_user_whiteboards_endpoint),
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify(buildPayload(withAi))
				}
			);
			const data = await response.json();
			if (!response.ok || !data.success) {
				throw new Error(data.error || 'Save failed.');
			}
			mergeRowsIntoPanels(data.data);
			const count = Array.isArray(data.data) ? data.data.length : 0;
			setStatusMessage(
				withAi
					? `Saved ${count} whiteboard${count === 1 ? '' : 's'} with AI.`
					: `Saved ${count} whiteboard${count === 1 ? '' : 's'}.`
			);
		} catch (err) {
			setErrorMessage(err.message || 'Save failed.');
		} finally {
			if (withAi) {
				setIsSavingAi(false);
			} else {
				setIsSaving(false);
			}
		}
	};

	const handleSave = () => {
		if (!user?.id || !endpoints?.upsert_user_whiteboards_endpoint) return;
		postWhiteboards({ withAi: false });
	};

	const handleSaveWithAi = () => {
		if (!user?.id || !endpoints?.upsert_user_whiteboards_endpoint) return;
		postWhiteboards({ withAi: true });
	};

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="mt-4 flex items-center justify-between gap-4">
				<PageTitle title="Whiteboard" />
				<div className="flex items-center gap-2">
					{boards.length > 1 ? (
						<button
							type="button"
							onClick={removeWhiteboard}
							className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-blue-500 text-2xl leading-none text-white transition-transform hover:scale-105"
							title="Remove last whiteboard"
						>
							−
						</button>
					) : null}
					<div className="text-sm text-gray-600 dark:text-gray-300">
						{boards.length}/{MAX_WHITEBOARDS}
					</div>
					{boards.length < MAX_WHITEBOARDS ? (
						<button
							type="button"
							onClick={addWhiteboard}
							className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-blue-500 text-2xl leading-none text-white transition-transform hover:scale-105"
							title="Add whiteboard"
						>
							+
						</button>
					) : null}
				</div>
			</div>

			<div
				className="relative mt-4 overflow-hidden rounded-lg bg-white shadow-lg dark:bg-dark-card"
				style={landingHeight}
			>
				<div className="relative h-full min-h-0 w-full p-3 md:p-4">
					<div className="relative h-full min-h-0">
						<div
							className={`grid h-full min-h-0 gap-3 overflow-y-auto ${
								boards.length === 1
									? 'grid-cols-1'
									: boards.length === 2
										? 'grid-cols-1 md:grid-cols-2'
										: 'grid-cols-1 md:grid-cols-3'
							}`}
						>
							{boards.map((board, index) => (
								<textarea
									key={`whiteboard-${index}`}
									value={board}
									onChange={(event) => updateBoard(index, event.target.value)}
									placeholder={`Whiteboard ${index + 1}`}
									className="h-full w-full resize-none rounded-md border border-gray-300 bg-white p-3 text-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-dark-border dark:bg-dark-background dark:text-dark-text"
									style={{ minHeight: '140px' }}
								/>
							))}
						</div>
						<div className="pointer-events-none absolute inset-0 z-30 flex items-end justify-center pb-2">
							<div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2 rounded-full bg-black px-3 py-1.5 text-white shadow-lg bg-opacity-80">
								<button
									type="button"
									onClick={handleSaveWithAi}
									disabled={isSavingAi || isSaving || !user?.id}
									className="rounded-md bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isSavingAi ? 'Saving with AI…' : 'Save with AI'}
								</button>
								<button
									type="button"
									onClick={handleSave}
									disabled={isSaving || isSavingAi || !user?.id}
									className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{isSaving ? 'Saving…' : 'Save'}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			{statusMessage ? (
				<p className="mt-2 text-sm text-green-700 dark:text-green-400">{statusMessage}</p>
			) : null}
			{errorMessage ? (
				<p className="mt-2 text-sm text-red-700 dark:text-red-400">{errorMessage}</p>
			) : null}
		</div>
	);
}
